import { useState, useEffect } from "react";
import { AxiosError } from "axios";
import { useGetBondApplicantFormSchema } from "./useBondFormSchema";
import { useGetBondBusinessFormSchema } from "./useBondBusinessFormSchema";
import { useGetBondRiskAssessmentFormSchema } from "./useBondRiskAssessmentSchema";
import { useBondIndemnitorFormSchema } from "./useBondIndemnitorSchema";
import { useBondOptionsSchema } from "./useBondOptionsSchema";
import { useProductSchema } from "./useProductSchema";
import { useProduct } from "./useProduct";
import { useStatesInfo } from "./useStatesInfo";
import { useGetBondBasicSchema } from "./useBondBasicSchema";
import { Product } from "../api/services/ProductsService";
import { useGetBondAgencyQuestionsSchema } from "./useBondAgencyQuestionsSchema";
import BondAgencyQuestionsSchemaParser from "./utils/BondAgencyQuestionsSchemaParser";
import { isServiceUnavailableStatus } from "../helpers/serviceUnavailable";

const useFormSchema = () => {
    const [product, setProduct] = useState<Product>();
    const [stateInfo, setStateInfo] = useState(null);
    const [bondApplicantFormSchema, setBondApplicantFormSchema] = useState(null);
    const [bondBusinessFormSchema, setBondBusinessSchema] = useState(null);
    const [bondRiskAssessmentFormSchema, setBondRiskAssessmentFormSchema] =
        useState(null);
    const [bondIndemnitorFormSchema, setBondIndemnitorFormSchema] =
        useState(null);
    const [bondOptionsFormSchema, setBondOptionsFormSchema] = useState(null);
    const [productFormSchema, setProductFormSchema] = useState(null);
    const [bondBasicFormSchema, setBondBasicSchema] = useState(null);
    const [bondAgencyQuestionsFormSchema, setBondAgencyQuestionsFormSchema] = useState(null);
    const [bondAgencyQuestionsFormUISchema, setBondAgencyQuestionsFormUISchema] = useState(null);

    const url: URL = new URL(window.location.href);
    const productID: string | null = url.searchParams.get("id");
    const sc: string | null = url.searchParams.get("sc");

    // Get product
    const {
        data: productData,
        error: productError,
        isError: isProductError,
        refetch: fetchProduct,
        isLoading: isProductLoading,
    } = useProduct("product", productID);

    // Get States
    const { stateData, fetchState } = useStatesInfo("state", sc);

    // Get JSON schema for bond form
    const {
        bondAppSchema,
        fetchBondAppSchema,
        isBondAppSchemaError,
        isBondAppSchemaLoading,
    } = useGetBondApplicantFormSchema("bondFormSchema");

    // Get JSON schema for bond business form
    const {
        bondBusinessSchema,
        fetchBondBusinessSchema,
        isBondBusinessSchemaError,
        isBondBusinessSchemaLoading,
    } = useGetBondBusinessFormSchema("bondBusinessSchema");

    const {
        bondBasicSchema,
        isBondBasicSchemaError,
        fetchBondBasicSchema,
        isBondBasicSchemaLoading,
    } = useGetBondBasicSchema("bondBasicSchema");

    // Get JSON schema for bond risk assessment form
    const {
        bondRiskAssessmentSchema,
        fetchBondRiskAssessmentSchema,
        isBondRiskAssessmentSchemaError,
        isBondRiskAssessmentSchemaLoading,
    } = useGetBondRiskAssessmentFormSchema("bondRiskAssessmentSchema");

    // Get JSON schema for bond indemnitor form
    const {
        bondIndemnitorSchema,
        fetchBondIndemnitorSchema,
        isBondIndemnitorSchemaLoading,
        isBondIndemnitorSchemaError,
    } = useBondIndemnitorFormSchema("bondIndemnitorSchema");

    // Get JSON schema for bond options form
    const {
        bondOptionsSchema,
        isBondOptionsSchemaLoading,
        isBondOptionsSchemaError,
        fetchBondOptionsSchema,
    } = useBondOptionsSchema("bondOptionsSchema");

    // Get JSON schema for product form
    const {
        productSchema,
        fetchProductSchema,
        isProductSchemaError,
        isProductSchemaLoading,
    } = useProductSchema("productSchema", productID);

    // Get JSON schema for agency questions
    const {
        bondAgencyQuestionsSchema,
        fetchBondAgencyQuestionsSchema,
        bondAgencyQuestionsUISchema,
        isBondAgencyQuestionsSchemaLoading,
        isBondAgencyQuestionsSchemaError
    } = useGetBondAgencyQuestionsSchema('bondAgencyQuestionsSchema')

    useEffect(() => {
        if (
            !bondAppSchema ||
            !bondBusinessSchema ||
            !bondRiskAssessmentSchema ||
            !bondIndemnitorSchema ||
            !bondOptionsSchema ||
            !bondBasicSchema
        )
            return;

        const getBasicSchema = () => {
            const basicSchemaKeys = Object.keys(bondBasicSchema.properties);
            let basicShema = bondBasicSchema;
            basicSchemaKeys.forEach((key) => {
                if (!basicSchemaKeys.includes("is_business")) {
                    basicShema.properties["is_business"] = {
                        format: "",
                        title: "Is Business?",
                        type: "boolean",
                    };
                }
            });

            return basicShema;
        };

        const changedBasicSchema = getBasicSchema();

        setBondBasicSchema(changedBasicSchema);
        setProduct(productData);
        setBondApplicantFormSchema(bondAppSchema);
        setBondBusinessSchema(bondBusinessSchema);
        setBondRiskAssessmentFormSchema(bondRiskAssessmentSchema);
        setBondIndemnitorFormSchema(bondIndemnitorSchema);
        setBondOptionsFormSchema(bondOptionsSchema);
        if (sc) {
            setStateInfo(stateData);
        }
        setProductFormSchema(productSchema);
        setBondAgencyQuestionsFormSchema(BondAgencyQuestionsSchemaParser.parse(bondAgencyQuestionsSchema) as any);
        setBondAgencyQuestionsFormUISchema(bondAgencyQuestionsUISchema);
    }, [
        bondAppSchema,
        bondBusinessSchema,
        bondBasicSchema,
        bondRiskAssessmentSchema,
        bondIndemnitorSchema,
        bondOptionsSchema,
        productSchema,
        stateData,
        productData,
        productID,
        sc,
        bondAgencyQuestionsSchema,
        bondAgencyQuestionsUISchema
    ]);

    const isBondLoading =
        isBondAppSchemaLoading ||
        isBondBasicSchemaLoading ||
        isBondBusinessSchemaLoading ||
        isBondIndemnitorSchemaLoading ||
        isBondOptionsSchemaLoading ||
        isBondRiskAssessmentSchemaLoading ||
        isProductLoading ||
        isProductSchemaLoading ||
        isBondAgencyQuestionsSchemaLoading;

    const isBondError =
        isBondBusinessSchemaError ||
        isBondAppSchemaError ||
        isBondBasicSchemaError ||
        isBondRiskAssessmentSchemaError ||
        isBondIndemnitorSchemaError ||
        isBondOptionsSchemaError ||
        isProductSchemaError ||
        isProductError ||
        isBondAgencyQuestionsSchemaError;

    useEffect(() => {
        if (!productError) return;

        if (isServiceUnavailableStatus(
            (productError as AxiosError)?.response?.status
        ))
            return;

        window.location.href = "/404";
    }, [productError]);

    return {
        state: {
            product,
            stateInfo,
            bondApplicantFormSchema,
            bondBusinessFormSchema,
            bondRiskAssessmentFormSchema,
            bondIndemnitorFormSchema,
            bondOptionsFormSchema,
            bondBasicFormSchema,
            productFormSchema,
            bondAgencyQuestionsFormSchema,
            bondAgencyQuestionsFormUISchema,
            isBondLoading,
            isBondError,
        },
        mutations: {
            setProduct,
            setStateInfo,
            setBondApplicantFormSchema,
            setBondBusinessSchema,
            setBondRiskAssessmentFormSchema,
            setBondBasicSchema,
            setBondIndemnitorFormSchema,
            setBondOptionsFormSchema,
            setProductFormSchema,
            setBondAgencyQuestionsFormSchema,
            setBondAgencyQuestionsFormUISchema,
        },
        actions: {
            fetchProduct,
            fetchState,
            fetchBondAppSchema,
            fetchBondBusinessSchema,
            fetchBondBasicSchema,
            fetchBondRiskAssessmentSchema,
            fetchBondIndemnitorSchema,
            fetchBondOptionsSchema,
            fetchProductSchema,
            fetchBondAgencyQuestionsSchema
        },
    };
};

export type FormSchema = ReturnType<typeof useFormSchema>;

export default useFormSchema;
